import { supabase, edgeFunctions } from '../lib/supabase'
import type {
  BrokerQuoteLookup,
  BrokerQuoteMaps,
  HelpdeskQuote,
  HelpdeskQuoteObs,
  HelpdeskQuoteObsSummary,
  HelpdeskQuoteObservation,
  HelpdeskQuoteStatusHistory,
  InsertQuoteObservationInput,
  ListQuotesFilters,
} from '../types/comercialCotacoes'
import {
  formatResponsibleDisplayName,
  normalizeResponsibleKey,
} from '../utils/cotacaoFormatters'

export type { ListQuotesFilters }

const PAGE_SIZE = 1000
const BROKER_ID_CHUNK_SIZE = 200
const QUOTE_OBS_ID_CHUNK_SIZE = 400
export const QUOTE_OBSERVATION_MAX_LENGTH = 4000
const QUOTE_OBSERVATION_QG_SUFFIX = ' (Via QG)'
export const QUOTE_OBSERVATION_MAX_USER_LENGTH =
  QUOTE_OBSERVATION_MAX_LENGTH - QUOTE_OBSERVATION_QG_SUFFIX.length

function normalizeQuote(row: Record<string, unknown>): HelpdeskQuote {
  return {
    id: String(row.id),
    quote_number: Number(row.quote_number),
    version: Number(row.version),
    responsible: (row.responsible as string | null) ?? null,
    company: (row.company as string | null) ?? null,
    state_code: (row.state_code as string | null) ?? null,
    city: (row.city as string | null) ?? null,
    status: (row.status as string | null) ?? null,
    inclusion_at: (row.inclusion_at as string | null) ?? null,
    broker_id: (row.broker_id as string | null) ?? null,
    insured_count: row.insured_count != null ? Number(row.insured_count) : null,
    requester_name: (row.requester_name as string | null) ?? null,
    requester_phone: (row.requester_phone as string | null) ?? null,
    requester_email: (row.requester_email as string | null) ?? null,
    amount: row.amount != null ? Number(row.amount) : null,
    amount_per_life: row.amount_per_life != null ? Number(row.amount_per_life) : null,
    commission: row.commission != null ? Number(row.commission) : null,
    notes: (row.notes as string | null) ?? null,
    updated_at: String(row.updated_at),
    minimum_incoice_amount:
      row.minimum_incoice_amount != null ? Number(row.minimum_incoice_amount) : null,
    total_premium:
      row.total_premium != null
        ? Number(row.total_premium)
        : row.total_premiun != null
          ? Number(row.total_premiun)
          : null,
    obs_count: row.obs_count != null ? Number(row.obs_count) : 0,
    last_created_at: (row.last_created_at as string | null) ?? null,
  }
}

function normalizeSearchRow(row: unknown): HelpdeskQuote {
  if (row && typeof row === 'object' && !Array.isArray(row)) {
    return normalizeQuote(row as Record<string, unknown>)
  }
  if (typeof row === 'string') {
    try {
      return normalizeQuote(JSON.parse(row) as Record<string, unknown>)
    } catch {
      // fall through
    }
  }
  return normalizeQuote({})
}

function buildSearchRpcParams(filters: ListQuotesFilters = {}) {
  const cotacao = filters.cotacao?.trim() || null
  const cliente = filters.cliente?.trim() || null
  const corretor = filters.corretor?.trim() || null
  const status =
    filters.status && filters.status.length > 0
      ? filters.status.map((s) => s.trim()).filter(Boolean)
      : null

  const origem = (filters.responsavelOrigem ?? 'all').trim().toLowerCase() || 'all'
  const pessoas = (filters.responsavelPessoas ?? []).map((p) => p.trim()).filter(Boolean)
  let responsavel: string | null = null
  let responsavelPessoas: string[] | null = null
  if (origem === 'portal' || origem === 'central') {
    responsavel = origem
    if (origem === 'central' && pessoas.length > 0) {
      responsavelPessoas = pessoas
    }
  }

  const comercialUserId =
    filters.comercialUserId && filters.comercialUserId !== 'all'
      ? filters.comercialUserId
      : null
  const dataInicio = filters.dataInicio?.trim() || null
  const dataFim = filters.dataFim?.trim() || null
  const statusHistorico = filters.statusHistorico?.trim() || null
  const statusHistoricoInicio = filters.statusHistoricoInicio?.trim() || null
  const statusHistoricoFim = filters.statusHistoricoFim?.trim() || null
  const hasStatusHistoryPeriod = !!(statusHistoricoInicio || statusHistoricoFim)

  return {
    p_cotacao: cotacao,
    p_cliente: cliente,
    p_corretor: corretor,
    p_status: status && status.length > 0 ? status : null,
    p_responsavel: responsavel,
    p_responsavel_pessoas: responsavelPessoas,
    p_comercial_user_id: comercialUserId,
    p_inclusion_from: dataInicio,
    p_inclusion_to: dataFim,
    p_status_history: statusHistorico && hasStatusHistoryPeriod ? statusHistorico : null,
    p_status_history_from: statusHistorico && hasStatusHistoryPeriod ? statusHistoricoInicio : null,
    p_status_history_to: statusHistorico && hasStatusHistoryPeriod ? statusHistoricoFim : null,
  }
}

export function buildBrokerQuoteMaps(brokers: BrokerQuoteLookup[]): BrokerQuoteMaps {
  const razaoById = new Map<string, string>()
  const nomeById = new Map<string, string>()

  for (const broker of brokers) {
    const holder = (broker.holder_name ?? '').trim()
    const name = (broker.name ?? '').trim()
    nomeById.set(broker.id, holder || name || '—')
    razaoById.set(broker.id, name || holder || '—')
  }

  return { razaoById, nomeById }
}

export function getBrokerRazao(
  brokerId: string | null | undefined,
  maps: BrokerQuoteMaps
): string {
  if (!brokerId) return 'Corretor não encontrado'
  return maps.razaoById.get(brokerId) ?? 'Corretor não encontrado'
}

export function parseEdgeFunctionError(error: unknown, data: unknown): string {
  const payload = (data ?? {}) as Record<string, unknown>
  if (typeof payload.error === 'string' && payload.error.trim()) return payload.error
  if (typeof payload.message === 'string' && payload.message.trim()) return payload.message

  const err = error as { message?: string; status?: number; context?: { status?: number } } | null
  const status = err?.status ?? err?.context?.status
  const message = (err?.message ?? '').toLowerCase()

  if (status === 401 || message.includes('jwt') || message.includes('unauthorized')) {
    return 'Sessão expirada. Faça login novamente.'
  }
  if (status === 404 || message.includes('not found')) {
    return 'Cotação não encontrada.'
  }
  if (status === 503 || status === 502 || message.includes('fetch')) {
    return 'Serviço indisponível no momento. Tente novamente em instantes.'
  }
  if (err?.message) return err.message
  return 'Não foi possível concluir a operação.'
}

const QUOTE_OBSERVATION_ERROR_MESSAGES: Record<string, string> = {
  invalid_json: 'Dados inválidos. Tente novamente.',
  invalid_quote_id: 'Cotação inválida.',
  empty_observacao: 'Informe o texto da observação.',
  observacao_too_long: `A observação deve ter no máximo ${QUOTE_OBSERVATION_MAX_LENGTH} caracteres.`,
  unauthorized: 'Sessão expirada. Faça login novamente.',
  forbidden: 'Você não tem permissão para alterar esta cotação.',
  origin_not_allowed: 'Origem não autorizada para esta operação.',
  internal_error: 'Não foi possível buscar os dados da cotação.',
  audit_log_failed: 'Observação enviada, mas falhou o registro local. Contate o suporte.',
  pasi_observacao_http: 'O serviço PASI retornou erro ao inserir a observação.',
  pasi_observacao_rejected: 'A observação foi rejeitada pelo PASI.',
  pasi_token_http: 'Falha ao autenticar no PASI.',
  pasi_token_body: 'Resposta inválida ao autenticar no PASI.',
  usuario_wiz_scgp_missing: 'Código SCGP do usuário não configurado.',
  usuario_wiz_scgp_invalid: 'Código SCGP do usuário inválido.',
  pasi_secrets_missing: 'Configuração do servidor incompleta.',
  pasi_url_api_missing: 'Configuração do servidor incompleta.',
}

function mapQuoteObservationError(
  error: unknown,
  data: unknown,
  detail?: string | null
): string {
  const payload = (data ?? {}) as Record<string, unknown>
  const code =
    (typeof payload.error === 'string' && payload.error.trim()) ||
    (typeof payload.message === 'string' && payload.message.trim()) ||
    ''

  if (code && QUOTE_OBSERVATION_ERROR_MESSAGES[code]) {
    const base = QUOTE_OBSERVATION_ERROR_MESSAGES[code]
    const extra = detail?.trim() || (typeof payload.detail === 'string' ? payload.detail.trim() : '')
    return extra ? `${base} ${extra}` : base
  }

  return parseEdgeFunctionError(error, data)
}

export function validateQuoteObservationInput(
  observacao: string,
  codUsuario: number | null | undefined
): string | null {
  const text = observacao.trim()
  if (!text) return 'Informe o texto da observação.'
  if (text.length > QUOTE_OBSERVATION_MAX_USER_LENGTH) {
    return `A observação deve ter no máximo ${QUOTE_OBSERVATION_MAX_USER_LENGTH} caracteres.`
  }
  const cod = codUsuario != null ? Math.trunc(Number(codUsuario)) : NaN
  if (!Number.isFinite(cod) || cod < 1) {
    return 'Código SCGP do usuário não está cadastrado. Solicite ao administrador.'
  }
  return null
}

function openOrDownloadPdf(url: string, fileName: string): void {
  if (/^https?:\/\//i.test(url)) {
    window.open(url, '_blank', 'noopener,noreferrer')
    return
  }

  if (url.startsWith('data:application/pdf') || url.startsWith('data:application/octet-stream')) {
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    return
  }

  window.open(url, '_blank', 'noopener,noreferrer')
}

export const comercialCotacoesService = {
  /**
   * Lista cotações via get_comercial_cotacoes_quotes_search (filtros no servidor).
   */
  async listQuotes(filters: ListQuotesFilters = {}): Promise<HelpdeskQuote[]> {
    const allRows: HelpdeskQuote[] = []
    let offset = 0
    let hasMore = true
    const filterParams = buildSearchRpcParams(filters)

    while (hasMore) {
      const { data, error } = await supabase.rpc('get_comercial_cotacoes_quotes_search', {
        p_limit: PAGE_SIZE,
        p_offset: offset,
        ...filterParams,
      })

      if (error) throw error

      const batch = ((data ?? []) as unknown[]).map(normalizeSearchRow)
      allRows.push(...batch)

      if (batch.length < PAGE_SIZE) {
        hasMore = false
      } else {
        offset += PAGE_SIZE
      }
    }

    return allRows
  },

  /**
   * Nomes distintos de `helpdesk_quotes.responsible` (título amigável).
   * RPC get_comercial_cotacoes_quote_responsibles.
   */
  async listDistinctQuoteResponsibles(): Promise<string[]> {
    const { data, error } = await supabase.rpc('get_comercial_cotacoes_quote_responsibles')
    if (error) throw error

    const byKey = new Map<string, string>()
    for (const row of (data ?? []) as Array<{ responsible?: string } | string>) {
      const raw =
        typeof row === 'string'
          ? row.trim()
          : typeof row?.responsible === 'string'
            ? row.responsible.trim()
            : ''
      if (!raw) continue
      const key = normalizeResponsibleKey(raw)
      if (key && !byKey.has(key)) {
        byKey.set(key, formatResponsibleDisplayName(raw))
      }
    }

    return Array.from(byKey.values()).sort((a, b) => a.localeCompare(b, 'pt-BR'))
  },

  /** Nomes de corretores referenciados nas cotações (evita varrer a tabela brokers inteira). */
  async listBrokersLookupByIds(brokerIds: string[]): Promise<BrokerQuoteLookup[]> {
    const uniqueIds = Array.from(new Set(brokerIds.filter(Boolean)))
    if (uniqueIds.length === 0) return []

    const brokers: BrokerQuoteLookup[] = []
    for (let i = 0; i < uniqueIds.length; i += BROKER_ID_CHUNK_SIZE) {
      const chunkIds = uniqueIds.slice(i, i + BROKER_ID_CHUNK_SIZE)
      const { data, error } = await supabase
        .from('brokers')
        .select('id, holder_name, name')
        .in('id', chunkIds)

      if (error) throw error
      brokers.push(...((data ?? []) as BrokerQuoteLookup[]))
    }

    return brokers
  },

  async getLastObservation(quoteId: string): Promise<HelpdeskQuoteObservation | null> {
    const { data: directData, error: directError } = await supabase
      .from('helpdesk_quote_observations')
      .select('id, quote_id, noted_at, author_name, note_text, created_at')
      .eq('quote_id', quoteId)
      .order('noted_at', { ascending: false, nullsFirst: false })
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (!directError && directData) {
      return directData as HelpdeskQuoteObservation
    }

    const { data: rpcData, error: rpcError } = await supabase.rpc(
      'get_comercial_cotacoes_quote_last_observation',
      { p_quote_id: quoteId }
    )

    if (rpcError) {
      if (directError) throw directError
      throw rpcError
    }

    const row = Array.isArray(rpcData) ? rpcData[0] : rpcData
    return row ? (row as HelpdeskQuoteObservation) : null
  },

  /** Resumo (contagem e última data) de comentários Wiz/PASI por cotação. */
  async listQuoteObsSummaries(quoteIds: string[]): Promise<HelpdeskQuoteObsSummary[]> {
    const uniqueIds = Array.from(new Set(quoteIds.filter(Boolean)))
    if (uniqueIds.length === 0) return []

    const summaries: HelpdeskQuoteObsSummary[] = []
    for (let i = 0; i < uniqueIds.length; i += QUOTE_OBS_ID_CHUNK_SIZE) {
      const chunkIds = uniqueIds.slice(i, i + QUOTE_OBS_ID_CHUNK_SIZE)
      const { data, error } = await supabase.rpc('get_comercial_cotacoes_quote_obs_summary', {
        p_quote_ids: chunkIds,
      })
      if (error) throw error

      for (const row of (data ?? []) as Array<Record<string, unknown>>) {
        summaries.push({
          quote_id: String(row.quote_id),
          obs_count: Number(row.obs_count) || 0,
          last_created_at: (row.last_created_at as string | null) ?? null,
        })
      }
    }

    return summaries
  },

  /** Histórico de comentários Wiz/PASI de uma cotação (mais recente primeiro). */
  async listQuoteObs(quoteId: string): Promise<HelpdeskQuoteObs[]> {
    if (!quoteId) return []

    const { data, error } = await supabase.rpc('get_comercial_cotacoes_quote_obs_list', {
      p_quote_id: quoteId,
    })
    if (error) throw error

    return ((data ?? []) as Array<Record<string, unknown>>).map((row) => ({
      id: String(row.id),
      quote_id: String(row.quote_id),
      quote_number: Number(row.quote_number),
      version: Number(row.version),
      observation: String(row.observation ?? ''),
      created_at: String(row.created_at),
      created_by: String(row.created_by),
    }))
  },

  /** Histórico de status da cotação (mais recente primeiro). */
  async listQuoteStatusHistory(quoteId: string): Promise<HelpdeskQuoteStatusHistory[]> {
    if (!quoteId) return []

    const { data, error } = await supabase.rpc('get_comercial_cotacoes_quote_status_history', {
      p_quote_id: quoteId,
    })
    if (error) throw error

    const rows = ((data ?? []) as Array<Record<string, unknown>>).map((row) => ({
      id: String(row.id),
      quote_id: String(row.quote_id),
      occurred_at: (row.occurred_at as string | null) ?? null,
      status_description: (row.status_description as string | null) ?? null,
      responsible: (row.responsible as string | null) ?? null,
      created_at: String(row.created_at),
    }))

    const timeOrZero = (value: string | null) => {
      if (!value) return 0
      const time = new Date(value).getTime()
      return Number.isNaN(time) ? 0 : time
    }

    return rows.sort((a, b) => {
      const byOccurred = timeOrZero(b.occurred_at) - timeOrZero(a.occurred_at)
      if (byOccurred !== 0) return byOccurred
      return timeOrZero(b.created_at) - timeOrZero(a.created_at)
    })
  },

  async downloadQuotePdf(
    quote: Pick<HelpdeskQuote, 'id' | 'quote_number' | 'version'>
  ): Promise<void> {
    const { data, error } = await edgeFunctions.invoke('PegarPDFCotacaoSCGP', {
      quote_id: quote.id,
    })

    if (error) {
      throw new Error(parseEdgeFunctionError(error, data))
    }

    const payload = (data ?? {}) as { url?: string; error?: string; message?: string }
    if (payload.error || payload.message) {
      throw new Error(parseEdgeFunctionError(null, payload))
    }

    const url = payload.url?.trim()
    if (!url) {
      throw new Error('Resposta inválida do serviço de PDF.')
    }

    const fileName = `cotacao-${quote.quote_number}-v${quote.version}.pdf`
    openOrDownloadPdf(url, fileName)
  },

  async insertQuoteObservation(input: InsertQuoteObservationInput): Promise<string> {
    const validationError = validateQuoteObservationInput(input.observacao, input.codUsuario)
    if (validationError) {
      throw new Error(validationError)
    }

    const observacao = `${input.observacao.trim()}${QUOTE_OBSERVATION_QG_SUFFIX}`
    const codUsuario = Math.trunc(input.codUsuario)

    const { data, error } = await edgeFunctions.invoke('InserirObservacaoCotacaoPASI', {
      quote_id: input.quoteId,
      observacao,
      cod_usuario: codUsuario,
    })

    if (error) {
      throw new Error(mapQuoteObservationError(error, data))
    }

    const payload = (data ?? {}) as {
      ok?: boolean
      id?: string
      error?: string
      detail?: string
    }

    if (!payload.ok || typeof payload.id !== 'string' || !payload.id.trim()) {
      throw new Error(mapQuoteObservationError(null, payload, payload.detail))
    }

    return payload.id
  },
}
